import { getEnvironment } from "./utils/environment";

export interface AuthConfig {
  domain: string;
  clientId: string;
  audience: string;
  authority: string;
}

export interface TelemetryConfig {
  sentryDns: string;
  otelServiceName: string;
  samplingRatio: number;
}

interface BasicConfig {
  appInsightsConnectionString: string;
  api: string;
  apimKey: string;
  authApi: string;
  auth0: AuthConfig;
  telemetryConfig: TelemetryConfig;
  cronitorClientKey: string;
  launchDarklyClientSideId: string;
}

export interface Config extends BasicConfig {
  environment: string;
  formattedEnvironment?: string;
}

// To develop against another environment, set the following in .env.local
// REACT_APP_APP_USE_ENVIRONMENT=[smoke|uat|production]
// eg. REACT_APP_APP_USE_ENVIRONMENT=smoke
const environment = process.env.REACT_APP_USE_ENVIRONMENT ?? getEnvironment();

// Set your own personal app insights connection string in .env.local
// The connection string is not considered a secret:
// https://learn.microsoft.com/en-us/azure/azure-monitor/app/sdk-connection-string?tabs=dotnet5#is-the-connection-string-a-secret
const localAppInsightsConnection =
  process.env.REACT_APP_APP_INSIGHTS_CONNECTION_STRING ??
  "InstrumentationKey=415e91cf-ed37-4151-aa96-6c87f1672113;IngestionEndpoint=https://uksouth-0.in.applicationinsights.azure.com;LiveEndpoint=https://uksouth.livediagnostics.monitor.azure.com/";

const configDefaults: BasicConfig = {
  appInsightsConnectionString: localAppInsightsConnection,
  api: "https://apidev.keoghs.co.uk/dtectdashapi/dtectdashapi",
  apimKey: "ef0477add2534f5eaba70e8a934ef761",
  telemetryConfig: {
    sentryDns:
      "https://c2c735b71308bed86e0b5e3b7a156a20@o4508126550884352.ingest.de.sentry.io/4508478396366928",
    otelServiceName: "dtection-dashboard",
    samplingRatio: 1.0,
  },
  authApi: "https://keo-uks-fds-uat-config.azurewebsites.net",
  auth0: {
    domain: "davies-group-uki-uat.uk.auth0.com",
    clientId: "X5RsJh72ZJrfY9R5W7IU35hrNoppdf8f",
    audience: "https://dtection-uat-api.com",
    authority: "https://davies-group-uki-uat.uk.auth0.com",
  },
  cronitorClientKey: "d7ea1a77750c3b8f66bd23507466eaf2",
  launchDarklyClientSideId: "67ffaa35cfc71608c3b4753d",
};

// Replace these values with the appropriate ones for your application
// You can add other environment specific config to this object.
const configOverrides: { [key: string]: BasicConfig } = {
  uat: {
    appInsightsConnectionString:
      "InstrumentationKey=ed70cb3c-1517-4946-b86a-b091c8485248;IngestionEndpoint=https://uksouth-0.in.applicationinsights.azure.com;LiveEndpoint=https://uksouth.livediagnostics.monitor.azure.com/",
    api: "https://apidev.keoghs.co.uk/dtectdashapi/dtectdashapi",
    apimKey: "ef0477add2534f5eaba70e8a934ef761",
    telemetryConfig: {
      sentryDns:
        "https://c2c735b71308bed86e0b5e3b7a156a20@o4508126550884352.ingest.de.sentry.io/4508478396366928",
      otelServiceName: "dtection-dashboard",
      samplingRatio: 1.0,
    },
    authApi: "https://keo-uks-fds-uat-config.azurewebsites.net",
    auth0: {
      domain: "davies-group-uki-uat.uk.auth0.com",
      clientId: "X5RsJh72ZJrfY9R5W7IU35hrNoppdf8f",
      audience: "https://dtection-uat-api.com",
      authority: "https://davies-group-uki-uat.uk.auth0.com",
    },
    cronitorClientKey: "d7ea1a77750c3b8f66bd23507466eaf2",
    launchDarklyClientSideId: "67ffaa35cfc71608c3b4753d",
  },
  production: {
    appInsightsConnectionString:
      "InstrumentationKey=ed70cb3c-1517-4946-b86a-b091c8485248;IngestionEndpoint=https://uksouth-0.in.applicationinsights.azure.com;LiveEndpoint=https://uksouth.livediagnostics.monitor.azure.com/",
    api: "https://api.keoghs.co.uk/dtectdashapi/dtectdashapi",
    apimKey: "14c0b4deb1a54943bda452986995b8a2",
    telemetryConfig: {
      sentryDns:
        "https://c2c735b71308bed86e0b5e3b7a156a20@o4508126550884352.ingest.de.sentry.io/4508478396366928",
      otelServiceName: "dtection-dashboard",
      samplingRatio: 1.0,
    },
    authApi: "https://keo-uks-fds-prod-config.azurewebsites.net",
    auth0: {
      domain: "davies-group-uki-prod.uk.auth0.com",
      clientId: "EXdRWu2b5FWkrQ2yxlsHINp2K5t1mtqS",
      audience: "https://dtection-prod-api.com",
      authority: "https://davies-group-uki-prod.uk.auth0.com",
    },
    cronitorClientKey: "d7ea1a77750c3b8f66bd23507466eaf2",
    launchDarklyClientSideId: "67ff87d76e45a508d93043eb",
  },
};

const populatedConfig: Config = {
  ...configDefaults,
  ...configOverrides[environment],
  environment,
};

export default populatedConfig;
